import type { GameState } from '../../engine/game-state.js';
import { createGrid, CLASSIC_LAYOUT } from '../../render/classic/layout.js';
import type { ClassicSpec } from '../../render/classic/types.js';
import { CARD_W, CARD_H } from '../../render/classic/types.js';
import {
  buildSpiderDeck,
  dealRow,
  FOUNDATION_IDS,
  isSameSuitDescendingRun,
  makeSpiderRules,
  type SpiderSuitCount,
  TABLEAU_IDS,
} from './rules.js';

const COLS = 10;
const TOP_Y = 24;
const TABLEAU_Y = TOP_Y + CARD_H + CLASSIC_LAYOUT.ROW_GAP;
const FELT_W = COLS * CARD_W + (COLS - 1) * CLASSIC_LAYOUT.COL_GAP + 48;
// Spider deals 6 cards to each of cols 1-4 and 5 cards to cols 5-10, then
// a long descending run can be rebuilt. 24 fan-down cards is the budgeted
// worst case (matches the Three.js maxFanLength).
const FELT_H = TABLEAU_Y + CARD_H + 23 * CLASSIC_LAYOUT.FAN_DOWN + 32;

const layout = createGrid({
  cols: COLS,
  feltWidth: FELT_W,
  rows: [
    // stock far left, gap, then 8 foundations
    { y: TOP_Y, slots: ['stock', null, ...FOUNDATION_IDS] },
    { y: TABLEAU_Y, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    if (pile.type !== 'tableau') return [];
    const run = pile.cards.slice(idx);
    if (!isSameSuitDescendingRun(run)) return [];
    return run.map((c) => c.id);
  }
  return [];
}

export interface SpiderClassicOptions {
  suits?: SpiderSuitCount;
}

export function makeSpiderClassicSpec(opts: SpiderClassicOptions = {}): ClassicSpec {
  const suits: SpiderSuitCount = opts.suits ?? 4;
  const rules = makeSpiderRules({ suits });
  return {
    rules,
    feltWidth: FELT_W,
    feltHeight: FELT_H,
    deckFactory: () => buildSpiderDeck(suits),
    slotForPile: (id) => layout.slot(id),
    dropExclude: () => ['stock', ...FOUNDATION_IDS],
    dragStackIds,
    autoSendOrder: () => [],
    onPileClick(state, pile) {
      if (pile.id === 'stock') {
        if (!dealRow(state)) return false;
        state.movesCount++;
        return true;
      }
      return false;
    },
    statusText(state) {
      const stock = state.piles.stock!.cards.length;
      const dealsLeft = Math.floor(stock / TABLEAU_IDS.length);
      if (dealsLeft === 0) return null;
      return `${dealsLeft} deal${dealsLeft === 1 ? '' : 's'} left`;
    },
    cascadeCardIds(state) {
      const ids: string[] = [];
      for (const id of FOUNDATION_IDS) {
        for (const c of state.piles[id]!.cards) ids.push(c.id);
      }
      return ids;
    },
  };
}

export const spiderClassicSpec: ClassicSpec = makeSpiderClassicSpec({ suits: 4 });
